import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { v, FieldRow, Section } from '../../atoms/index.js';
/**
 * Document sections 1–9, shared by both templates (per ADR-0049). The
 * closing §10 (Bekräftelse / Underskrift) differs and is appended by each
 * template's own metadata + Body.
 */
export const SHARED_SECTION_DEFS = [
    { ref: 'personuppgiftsansvarig', title: 'Personuppgiftsansvarig' },
    { ref: 'andamal', title: 'Ändamål med behandlingen' },
    { ref: 'kategorier', title: 'Kategorier av personuppgifter' },
    { ref: 'rattslig-grund', title: 'Rättslig grund' },
    { ref: 'mottagare', title: 'Mottagare av personuppgifter' },
    { ref: 'lagringstid', title: 'Lagringstid' },
    { ref: 'rattigheter', title: 'Hyresgästens rättigheter' },
    { ref: 'klagomal', title: 'Klagomål' },
    { ref: 'kontakt', title: 'Kontakt' },
];
/**
 * Shared variable schema for the personuppgiftsansvarig + kontaktperson
 * fields. The landlord identity is sourced differently per variant:
 *
 *   - `'bilaga'`     — `derivedFromParent` (name/org/address overlaid from
 *     the parent lease; the three parents share these key names). E-post /
 *     telefon are left plain because not every parent template carries them.
 *   - `'standalone'` — autofilled from the `(lessor, 0)` party.
 *
 * The kontaktperson rows are plain freetext in both (no reliable source).
 */
export function buildSharedFields(variant) {
    const lessor = (field) => ({ kind: 'party', role: 'lessor', ordinal: 0, field });
    // Landlord identity — derived from the parent lease on the bilaga,
    // autofilled from the lessor party on the standalone.
    const landlord = (key, partyField) => variant === 'standalone'
        ? { autofill: lessor(partyField) }
        : { derivedFromParent: true, help: 'Hämtas från hyresavtalet.' };
    return [
        // — §1 Personuppgiftsansvarig (hyresvärd) —
        {
            key: 'landlord_name',
            label: 'Hyresvärd',
            type: 'text',
            section: 'personuppgiftsansvarig',
            requiredAtFinal: true,
            ...landlord('landlord_name', 'legal_name'),
        },
        {
            key: 'landlord_org_number',
            label: 'Organisationsnummer',
            type: 'text',
            section: 'personuppgiftsansvarig',
            requiredAtFinal: true,
            ...landlord('landlord_org_number', 'id_number'),
        },
        {
            key: 'landlord_address',
            label: 'Adress',
            type: 'textarea',
            section: 'personuppgiftsansvarig',
            requiredAtFinal: true,
            ...landlord('landlord_address', 'composed_address'),
        },
        {
            key: 'landlord_email',
            label: 'E-post',
            type: 'text',
            section: 'personuppgiftsansvarig',
            ...(variant === 'standalone'
                ? { autofill: lessor('email') }
                : {}),
        },
        {
            key: 'landlord_phone',
            label: 'Telefon',
            type: 'text',
            section: 'personuppgiftsansvarig',
            ...(variant === 'standalone'
                ? { autofill: lessor('phone') }
                : {}),
        },
        // — §9 Kontakt —
        {
            key: 'contact_person',
            label: 'Kontaktperson',
            type: 'text',
            section: 'kontakt',
            help: 'Person hos hyresvärden som hanterar frågor om personuppgiftsbehandlingen.',
        },
        {
            key: 'contact_phone',
            label: 'Telefon',
            type: 'text',
            section: 'kontakt',
        },
        {
            key: 'contact_email',
            label: 'E-post',
            type: 'text',
            section: 'kontakt',
        },
    ];
}
export const SHARED_SAMPLE_VALUES = {
    landlord_name: 'Bostadsägarna Stockholm AB',
    landlord_org_number: '556789-1234',
    landlord_address: 'Drottninggatan 100\n111 60 Stockholm',
    landlord_email: 'kontakt@bostadsagarna.se',
    landlord_phone: '08-123 45 67',
    contact_person: 'Dataskyddsombud, Bostadsägarna Stockholm AB',
    contact_phone: '08-123 45 60',
    contact_email: 'dataskydd@bostadsagarna.se',
};
function BulletList({ items }) {
    return (_jsx("ul", { className: "mt-1 list-disc pl-5 text-[10.5px] leading-relaxed text-stone-700", children: items.map((it) => (_jsx("li", { children: it }, it))) }));
}
/**
 * Sections 1–9 of the personuppgiftsbilaga, rendered inside the caller's
 * `<SectionNumberingProvider>`. The caller appends its own §10
 * (Bekräftelse / Underskrift). Static clause text is verbatim from the
 * approved Word source.
 */
export function PersonuppgiftSharedBody({ values, }) {
    return (_jsxs(_Fragment, { children: [_jsx("p", { className: "mb-4 text-[10.5px] leading-relaxed text-stone-700", children: "Denna bilaga utg\u00F6r information enligt artikel 13 i Europaparlamentets och r\u00E5dets f\u00F6rordning (EU) 2016/679 (GDPR)." }), _jsxs(Section, { sectionRef: "personuppgiftsansvarig", title: "Personuppgiftsansvarig", children: [_jsx("p", { className: "mb-2 text-[10.5px] leading-relaxed text-stone-700", children: "Personuppgiftsansvarig f\u00F6r behandlingen av personuppgifter \u00E4r:" }), _jsx(FieldRow, { label: "Hyresv\u00E4rd", children: v(values.landlord_name) }), _jsx(FieldRow, { label: "Organisationsnummer", children: v(values.landlord_org_number) }), _jsx(FieldRow, { label: "Adress", children: v(values.landlord_address) }), _jsx(FieldRow, { label: "E-post", children: v(values.landlord_email) }), _jsx(FieldRow, { label: "Telefon", children: v(values.landlord_phone) })] }), _jsxs(Section, { sectionRef: "andamal", title: "\u00C4ndam\u00E5l med behandlingen", children: [_jsx("p", { className: "text-[10.5px] leading-relaxed text-stone-700", children: "Hyresv\u00E4rden behandlar personuppgifter f\u00F6r att:" }), _jsx(BulletList, { items: [
                            'administrera hyresförhållandet,',
                            'fullgöra hyresavtalet,',
                            'hantera hyresbetalningar,',
                            'kommunicera med hyresgästen,',
                            'utföra felanmälningar och serviceärenden,',
                            'uppfylla rättsliga skyldigheter,',
                            'hantera tvister och rättsliga anspråk,',
                            'bedriva fastighetsförvaltning och säkerhetsarbete.',
                        ] })] }), _jsxs(Section, { sectionRef: "kategorier", title: "Kategorier av personuppgifter", children: [_jsx("p", { className: "text-[10.5px] leading-relaxed text-stone-700", children: "Hyresv\u00E4rden kan behandla f\u00F6ljande uppgifter:" }), _jsx(BulletList, { items: [
                            'namn,',
                            'personnummer eller samordningsnummer,',
                            'kontaktuppgifter,',
                            'adressuppgifter,',
                            'betalningsuppgifter,',
                            'uppgifter om hyresförhållandet,',
                            'uppgifter som lämnas i samband med felanmälningar eller andra kontakter med hyresvärden.',
                        ] })] }), _jsxs(Section, { sectionRef: "rattslig-grund", title: "R\u00E4ttslig grund", children: [_jsx("p", { className: "text-[10.5px] leading-relaxed text-stone-700", children: "Behandlingen sker med st\u00F6d av:" }), _jsx(BulletList, { items: [
                            'fullgörande av avtal,',
                            'rättslig förpliktelse,',
                            'berättigat intresse, samt, när så krävs enligt lag, samtycke från hyresgästen.',
                        ] })] }), _jsxs(Section, { sectionRef: "mottagare", title: "Mottagare av personuppgifter", children: [_jsx("p", { className: "text-[10.5px] leading-relaxed text-stone-700", children: "Personuppgifter kan komma att l\u00E4mnas ut till:" }), _jsx(BulletList, { items: [
                            'fastighetsförvaltare,',
                            'tekniska entreprenörer,',
                            'IT-leverantörer,',
                            'betalningsförmedlare,',
                            'inkassobolag,',
                            'myndigheter när lag eller myndighetsbeslut kräver detta.',
                        ] }), _jsx("p", { className: "mt-2 text-[10.5px] leading-relaxed text-stone-700", children: "Personuppgifter l\u00E4mnas inte ut till tredje man f\u00F6r marknadsf\u00F6rings\u00E4ndam\u00E5l utan s\u00E4rskilt st\u00F6d." })] }), _jsx(Section, { sectionRef: "lagringstid", title: "Lagringstid", children: _jsx("p", { className: "text-[10.5px] leading-relaxed text-stone-700", children: "Personuppgifter sparas s\u00E5 l\u00E4nge det \u00E4r n\u00F6dv\u00E4ndigt f\u00F6r att administrera hyresf\u00F6rh\u00E5llandet och d\u00E4refter under den tid som kr\u00E4vs enligt lag eller f\u00F6r att fastst\u00E4lla, g\u00F6ra g\u00E4llande eller f\u00F6rsvara r\u00E4ttsliga anspr\u00E5k." }) }), _jsxs(Section, { sectionRef: "rattigheter", title: "Hyresg\u00E4stens r\u00E4ttigheter", children: [_jsx("p", { className: "text-[10.5px] leading-relaxed text-stone-700", children: "Hyresg\u00E4sten har r\u00E4tt att:" }), _jsx(BulletList, { items: [
                            'begära registerutdrag,',
                            'begära rättelse,',
                            'begära radering när förutsättningar finns,',
                            'begära begränsning av behandling,',
                            'invända mot viss behandling,',
                            'begära dataportabilitet när lagens förutsättningar är uppfyllda.',
                        ] })] }), _jsx(Section, { sectionRef: "klagomal", title: "Klagom\u00E5l", children: _jsx("p", { className: "text-[10.5px] leading-relaxed text-stone-700", children: "Hyresg\u00E4sten har r\u00E4tt att l\u00E4mna klagom\u00E5l till Integritetsskyddsmyndigheten (IMY)." }) }), _jsxs(Section, { sectionRef: "kontakt", title: "Kontakt", children: [_jsx("p", { className: "mb-2 text-[10.5px] leading-relaxed text-stone-700", children: "Fr\u00E5gor om personuppgiftsbehandlingen kan st\u00E4llas till:" }), _jsx(FieldRow, { label: "Kontaktperson", children: v(values.contact_person) }), _jsx(FieldRow, { label: "Telefon", children: v(values.contact_phone) }), _jsx(FieldRow, { label: "E-post", children: v(values.contact_email) })] })] }));
}
/**
 * §10 Bekräftelse — the confirmation sentence, shared by both variants. On
 * the bilaga it stands alone (signing rides the parent); on the standalone
 * the Underskrift block follows it.
 */
export const BEKRAFTELSE_TEXT = 'Hyresgästen bekräftar genom sin underskrift att information om personuppgiftsbehandling har mottagits.';
//# sourceMappingURL=shared.js.map